import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatDate, openCheckoutSync, resolveCheckout } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasSubscriberAccess } from "@/lib/subscription";
import { PageHeader, Card, Button, Pill, Loader } from "@/components/ui/Primitives";
import StripeOpenModal from "@/components/StripeOpenModal";
import { Check, Rocket, TrendingUp, Crown, ExternalLink, ShieldCheck, Banknote } from "lucide-react";
import { toast } from "sonner";

const ICONS = { starter: Rocket, professional: TrendingUp, enterprise: Crown };

export default function Billing() {
  const { user, refresh } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [checkoutUrls, setCheckoutUrls] = useState({}); // { [planKey]: stripeUrl }
  const [modalUrl, setModalUrl] = useState("");
  const [modalLabel, setModalLabel] = useState("");
  const linkRefs = useRef({}); // ref map for the fallback inline <a>
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [verification, setVerification] = useState("");
  const verificationBusy = useRef(false);
  // Stripe Connect (landlords receiving rent via platform) was removed
  // in Feb 2026 for regulatory reasons — money transmission requires
  // FINTRAC/MSB registration. Landlords now publish their own payment
  // instructions and tenants pay them peer-to-peer, off-platform.

  // --- Checkout watcher -----------------------------------------------------
  // The external backend hardcodes Stripe's return URLs to its own domain, so
  // after finishing (or abandoning) checkout the Stripe tab can land on a
  // broken page. This watcher keeps THIS tab fresh instead: while a checkout
  // is in flight it polls /subscription/status, listens for tab re-focus, and
  // watches the popup. The moment the plan activates (or the user comes back)
  // the page state refreshes itself — no manual reload needed.
  const watchRef = useRef(null);
  const stopWatch = () => {
    const w = watchRef.current;
    if (!w) return;
    clearInterval(w.interval);
    window.removeEventListener("focus", w.onFocus);
    document.removeEventListener("visibilitychange", w.onVis);
    watchRef.current = null;
  };
  const startCheckoutWatch = (popup) => {
    stopWatch();
    const startedAt = Date.now();
    const check = async () => {
      if (!watchRef.current) return;
      if (Date.now() - startedAt > 15 * 60 * 1000) { stopWatch(); return; }
      try {
        const { data } = await api.get("/subscription/status");
        const st = data?.status || data?.subscription_status;
        if (st === "active" || st === "trialing") {
          stopWatch();
          setModalUrl("");
          setCheckoutUrls({});
          toast.success(st === "trialing" ? "Trial started — you're all set!" : "Subscription activated — welcome aboard!");
          await Promise.all([load(), refresh()]);
          return;
        }
      } catch {}
      if (popup && popup.closed && watchRef.current) {
        stopWatch();
        toast.info("Checkout window closed. Checking your subscription; do not pay again while verification is pending.");
        setVerification("pending");
        await Promise.all([load(), refresh()]);
      }
    };
    const interval = setInterval(check, 4000);
    const onFocus = () => check();
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    watchRef.current = { interval, onFocus, onVis };
  };
  useEffect(() => stopWatch, []);

  // After we receive a Stripe URL we render an <a target="_blank"> in
  // place of the button so the user can tap to open Checkout in a new
  // tab. We DO NOT auto-click — mobile browsers block popups when the
  // click isn't from a fresh user gesture. The popup-pre-open trick in
  // `subscribe()` handles the auto-open path during the original click.

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/subscription/plans").catch(() => ({ data: { plans: [] } })),
        api.get("/subscription/status").catch(() => ({ data: null })),
      ]);
      setPlans(p.data?.plans || []);
      setStatus(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); refresh().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyPayment = async (sid) => {
    if (verificationBusy.current) return;
    verificationBusy.current = true; setVerification("checking");
    const deadline = Date.now() + 20000;
    try {
      for (let attempt = 0; attempt < 4 && Date.now() < deadline; attempt += 1) {
        try {
          if (sid) await api.get(`/subscription/checkout/status/${sid}`, { timeout: Math.max(500, Math.min(5000, deadline - Date.now())) });
          const account = await refresh();
          if (hasSubscriberAccess(account)) {
            await load(); setVerification("confirmed"); setSearch({}, { replace: true });
            toast.success("Subscription verified — your plan features are available.");
            return;
          }
        } catch { /* A paid checkout can be waiting for provider synchronization. */ }
        if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      setVerification("pending");
    } finally { verificationBusy.current = false; }
  };

  // Stripe redirect-back handler — refresh subscription state and show a toast.
  useEffect(() => {
    if (search.get("checkout") === "success") {
      const sid = search.get("session_id");
      verifyPayment(sid);
    } else if (search.get("already_active") === "1") {
      // The duplicate-charge guard short-circuited — user already had an
      // active subscription. Reassure them + refresh state.
      toast.info("You already have an active subscription — no new charge was made.");
      setSearch({}, { replace: true });
      (async () => { await Promise.all([load(), refresh()]); })();
    } else if (search.get("checkout") === "cancelled") {
      toast.info("Checkout cancelled — no charge was made.");
      setSearch({}, { replace: true });
    }
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-launch the Stripe modal when arriving from /signup?plan=<key>.
  // The popup tab was already pre-opened during the signup click, but
  // signup's nav() loses that reference; the modal here gives the user a
  // tap-to-open Stripe button without a fresh click.
  useEffect(() => {
    const auto = search.get("autoOpen");
    const planKey = search.get("plan");
    if (auto === "1" && planKey && plans.length > 0 && !modalUrl) {
      // Fire the checkout flow — opens a sync popup (a fresh user-tap in
      // /signup's flow will fail because the click already finished), so
      // we fall back to the modal which is guaranteed.
      (async () => {
        try {
          const origin = window.location.origin;
          const { data } = await api.post("/subscription/checkout", {
            plan_key: planKey,
            success_url: `${origin}/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${origin}/billing?checkout=cancelled`,
          });
          if (data?.url) {
            setCheckoutUrls((prev) => ({ ...prev, [planKey]: data.url }));
            const planName = plans.find((p) => p.key === planKey)?.name || planKey;
            setModalLabel(planName + " plan");
            setModalUrl(data.url);
            startCheckoutWatch(null);
          } else {
            throw new Error("Checkout returned no URL");
          }
        } catch (err) {
          // Never fail silently here: a dropped auto-open checkout otherwise
          // shows up only as "registered but never paid".
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Could not start checkout. Choose your plan below to try again.");
          api.post("/client-errors", {
            kind: "checkout_autoopen",
            message: `POST /subscription/checkout (${planKey}) → ${err?.response?.status || err?.code || err?.message || "network"}`,
            path: window.location.pathname,
          }).catch(() => {});
        }
        setSearch({}, { replace: true });
      })();
    }
  }, [search, plans, modalUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (planKey) => {
    // CRITICAL: open the blank tab SYNCHRONOUSLY during the click so the
    // browser keeps the user-gesture context. Mobile Safari + iframe
    // sandboxes block window.open() after an async fetch resolves.
    const popup = openCheckoutSync();
    setCheckingOut(planKey);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/subscription/checkout", {
        plan_key: planKey,
        // MUST include the {CHECKOUT_SESSION_ID} placeholder so that
        // when Stripe redirects back to /billing?checkout=success we
        // can synchronously activate the account via
        // /api/subscription/checkout/status/{session_id} — this is
        // our belt-and-braces fallback for when the Stripe webhook
        // is slow, delayed, or misconfigured.
        success_url: `${origin}/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/billing?checkout=cancelled`,
      });
      if (data?.url) {
        // Stash URL so we always render a visible fallback <a target="_blank">.
        setCheckoutUrls((prev) => ({ ...prev, [planKey]: data.url }));
        const ok = resolveCheckout(popup, data.url);
        // ALWAYS show the modal as a guaranteed-tap path. Even if the
        // popup redirect succeeded, the modal stays open briefly so the
        // user can re-open Stripe if mobile Safari swallowed the popup.
        const planName = plans.find((p) => p.key === planKey)?.name || planKey;
        setModalLabel(planName + " plan");
        setModalUrl(data.url);
        startCheckoutWatch(popup);
        if (!ok) {
          toast.info("Tap the green button to open Stripe Checkout.");
        }
        setCheckingOut(null);
        return;
      }
      if (popup) { try { popup.close(); } catch {} }
      toast.error("Stripe checkout unavailable. Contact support.");
      setCheckingOut(null);
    } catch (err) {
      if (popup) { try { popup.close(); } catch {} }
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Could not start checkout";
      if (status === 503 || /stripe/i.test(detail || "")) {
        toast.error("Stripe is not yet configured for this account. Please contact support.");
      } else {
        toast.error(detail);
      }
      setCheckingOut(null);
    }
  };

  const openPortal = async () => {
    const popup = openCheckoutSync();
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscription/portal", {
        return_url: `${window.location.origin}/billing`,
      });
      if (data?.url) {
        setPortalUrl(data.url);
        resolveCheckout(popup, data.url);
        setModalLabel("Billing portal");
        setModalUrl(data.url);
        return;
      }
      if (popup) { try { popup.close(); } catch {} }
      toast.error("Billing portal unavailable.");
    } catch (err) {
      if (popup) { try { popup.close(); } catch {} }
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
    } finally { setPortalLoading(false); }
  };

  const cancel = async () => {
    const isTrial = status?.is_trial || user?.subscription_status === "trialing";
    const msg = isTrial
      ? "End your paid plan now? Your access will stop immediately and your card will NOT be charged."
      : "Cancel your subscription at the end of the billing period?";
    if (!confirm(msg)) return;
    try {
      const { data } = await api.post("/subscription/cancel");
      if (data?.ended_immediately) {
        toast.success("Trial ended — no charge was made.");
      } else {
        toast.success("Subscription will cancel at period end");
      }
      await Promise.all([load(), refresh()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };

  if (loading) return <Loader/>;

  const active = hasSubscriberAccess(user);

  return (
    <div>
      <StripeOpenModal url={modalUrl} amountLabel={modalLabel} onClose={() => setModalUrl("")} />
      <PageHeader
        title="Billing & subscription"
        subtitle="Unlock smart features by activating a plan. Payments are processed securely by Stripe."
        actions={active && (
          portalUrl ? (
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="open-portal-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800">
              <ExternalLink className="w-4 h-4"/> Open billing portal
            </a>
          ) : (
            <Button variant="secondary" onClick={openPortal} disabled={portalLoading} data-testid="manage-billing-button">
              <ExternalLink className="w-4 h-4"/> {portalLoading ? "Preparing…" : "Manage billing"}
            </Button>
          )
        )}
      />

      {verification && <div role="status" data-testid="billing-verification-status" className={`mb-6 rounded-md border p-4 text-sm ${verification === "confirmed" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-950"}`}>
        <p>{verification === "confirmed" ? "Your subscription is verified and access is available." : verification === "checking" ? "Verifying your subscription. Please do not start another checkout." : "Subscription verification is pending. Do not pay again. Check access or contact support with your payment receipt."}</p>
        {verification === "pending" && <Button variant="secondary" data-testid="billing-check-access" onClick={() => verifyPayment(search.get("session_id"))} className="mt-3">Check access</Button>}
      </div>}

      {user?.role === "landlord" && (
        <Card className="p-6 mb-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200" data-testid="rent-collection-notice">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-none">
              <Banknote className="w-5 h-5"/>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Rent collection</div>
              <div className="font-display text-xl font-bold text-slate-900 mt-1">
                Tenants pay you directly — no middleman
              </div>
              <p className="text-sm text-slate-700 mt-1 max-w-xl">
                Central Rentals never touches your rent money. Publish your payment
                instructions (Interac e-Transfer, bank details, or cheque address) once
                and tenants see them right inside their app.
              </p>
              <div className="mt-3">
                <Link to="/settings/payment-instructions" data-testid="edit-payment-instructions-link"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800">
                  <ExternalLink className="w-4 h-4"/> Edit payment instructions
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Current plan</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1 capitalize">
              {active ? (status?.plan_name || status?.plan_key || user?.subscription_status || "Active") : "No active subscription"}
            </div>
            {status?.is_trial && status?.trial_end && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full" data-testid="trial-active-pill">
                <ShieldCheck className="w-3.5 h-3.5"/>
                Paid plan — first charge {formatDate(status.trial_end)}
              </div>
            )}
            {status?.current_period_end && !status?.is_trial && (
              <div className="text-xs text-slate-500 mt-1">Renews on {formatDate(status.current_period_end)}</div>
            )}
            {!active && (
              <div className="text-sm text-slate-600 mt-2 max-w-xl">
                Smart features (Compliance, Utilities, Rent analysis, Tours marketing, Investor portal, Assistant) are available to active subscribers only.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Pill tone={active ? "green" : "amber"}>{user?.subscription_status || "inactive"}</Pill>
            {active && (
              <Button variant="secondary" onClick={cancel} data-testid="cancel-subscription-button">
                {status?.is_trial ? "Cancel trial" : "Cancel"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const Icon = ICONS[p.key] || Rocket;
          const highlight = p.key === "professional";
          const isCurrent = active && (status?.plan_key === p.key);
          return (
            <div key={p.key} data-testid={`billing-plan-${p.key}`}
              className={`p-6 rounded-2xl border ${highlight ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-white shadow-xl shadow-emerald-100/50" : "border-slate-200 bg-white shadow-sm"} relative`}>
              {highlight && (
                <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm">Most popular</div>
              )}
              {isCurrent && (
                <div className={`absolute -top-3 right-7 px-3 py-1 rounded-full bg-sky-600 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm`}>Current</div>
              )}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100`}>
                <Icon className="w-5 h-5"/>
              </div>
              <div className={`font-display text-xl font-bold text-slate-900`}>{p.name}</div>
              <div className={`text-sm mt-1 text-slate-500`}>{p.target_units}</div>
              <div className="mt-5 flex items-baseline gap-1" data-testid={`billing-plan-price-${p.key}`}>
                <span className={`font-display text-4xl font-bold text-slate-900`}>${(p.price_cents || 0) / 100}</span>
                <span className={`text-sm text-slate-500`}>/mo CAD</span>
              </div>
              <p className={`mt-3 text-sm text-slate-600`}>{p.tagline}</p>
              {checkoutUrls[p.key] ? (
                <a
                  ref={(el) => { linkRefs.current[p.key] = el; }}
                  href={checkoutUrls[p.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`open-stripe-${p.key}`}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-colors ring-2 ring-emerald-300 animate-pulse bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm`}
                >
                  Open Stripe Checkout <ExternalLink className="w-3.5 h-3.5"/>
                </a>
              ) : (
                <Button
                  variant={highlight ? "primary" : "secondary"}
                  className="w-full mt-5"
                  disabled={checkingOut === p.key || isCurrent}
                  onClick={() => subscribe(p.key)}
                  data-testid={`subscribe-plan-${p.key}`}
                >
                  {isCurrent
                    ? "Current plan"
                    : checkingOut === p.key
                      ? "Preparing…"
                      : <>Subscribe with Stripe <ExternalLink className="w-3.5 h-3.5"/></>}
                </Button>
              )}
              <ul className={`mt-6 space-y-2 text-sm text-slate-700`}>
                {(p.features || []).slice(0, 6).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className={`w-4 h-4 mt-0.5 flex-none text-emerald-600`}/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <Card className="p-5 mt-6 bg-slate-50 border-slate-200">
        <div className="flex items-start gap-3 text-sm text-slate-700">
          <ShieldCheck className="w-5 h-5 text-green-800 flex-none mt-0.5"/>
          <div>
            <div className="font-semibold text-slate-900">Powered by Stripe — secure & cancellable</div>
            <p className="text-slate-600 mt-1">All payments are processed by Stripe (PCI-DSS). We never see or store your card. Cancel anytime from Manage billing.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
